import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { PlayArrow, Delete } from '@mui/icons-material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import CustomLazyLoadImage from '../../../components/Image/CustomLazyLoadImage';
import { IProject } from '../../../types/global/helper';

interface MessageVideoThumbnailProps {
    title: string;
    thumbnailUrl?: string;
    projectId: string;
    project?: IProject; // Use proper interface instead of any
    onDelete?: () => void;
    onClick?: () => void;
    showControls?: boolean;
}

export const MessageVideoThumbnail: React.FC<MessageVideoThumbnailProps> = ({
    title,
    project, // The full project object
    onDelete,
    onClick,
    showControls = true,
}) => {
    const { themeStyle } = useThemeContext();

    // Both IProject and ISound have title property
    const displayTitle = project ? project.title : title;

    return (
        <Box
            sx={{
                position: 'relative',
                borderRadius: 1,
                overflow: 'hidden',
                width: '100%',
                height: 'auto',
                minWidth: 150,
                cursor: 'pointer',
                bgcolor: theme[themeStyle].deep_grey,
                '&:hover .controls': {
                    opacity: 1,
                },
            }}
            onClick={onClick}>
            <Box sx={{ position: 'relative', paddingTop: '56.25%' /* 16:9 aspect ratio */ }}>
                <CustomLazyLoadImage
                    src={project?.feature_image ?? project?.thumbnail}
                    alt={displayTitle}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />

                {/* Play button overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0,0,0,0.3)',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                    }}
                    className="controls">
                    <IconButton
                        sx={{
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.8)',
                            },
                        }}
                        onClick={e => {
                            e.stopPropagation();
                            onClick && onClick();
                        }}>
                        <PlayArrow />
                    </IconButton>
                </Box>
            </Box>

            {/* Title and delete control */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'rgba(0,0,0,0.7)',
                    p: 0.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'white',
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: onDelete ? 'calc(100% - 24px)' : '100%',
                    }}>
                    {displayTitle}
                </Typography>

                {showControls && onDelete && (
                    <IconButton
                        size="small"
                        onClick={e => {
                            e.stopPropagation();
                            onDelete();
                        }}
                        sx={{
                            color: 'white',
                            p: 0.2,
                            '&:hover': {
                                color: theme[themeStyle].highlight,
                            },
                        }}>
                        <Delete fontSize="small" />
                    </IconButton>
                )}
            </Box>
        </Box>
    );
};
